<template>
  <page-header-wrapper>
    <a-card :bordered="false">
      <a-alert :message="`${schema.title || '业务配置'}：数据直接读写原 DK 商城业务表，APP 接口继续使用同一份数据。`" type="info" show-icon />
      <a-alert v-if="moduleKey === 'delivery-time'" class="dialing-hint" message="这里是按现货/定制商城维护的全局默认发货时效。子商品已设置发货时效规则时，优先使用子商品规则；未设置时才使用这里的配置，不会叠加。" type="info" show-icon />
      <a-alert v-if="moduleKey === 'dialing-code'" class="dialing-hint" message="本配置仅作用于账号注册时的地区/区号选择（含国旗）。注册页显示全部启用国家/地区，支持中英文名及区号搜索；多个国家可共用区号。禁用或删除后不再供新注册选择，已有用户信息保留。排序越大越靠前。" type="info" show-icon />
      <a-alert v-if="moduleKey === 'address-label'" class="dialing-hint" message="维护所有用户可选择的公共地址标签。停用或删除后不再供新建地址选择，已保存地址的标签保留；用户自行添加的私人标签不在这里维护。" type="info" show-icon />
      <a-alert v-if="moduleKey === 'recommendation-preference'" class="dialing-hint" message="用户多选偏好时按关联分类的并集推荐，并自动包含子分类。探索页及查看推荐商品使用同一规则；没有有效偏好时展示通用推荐。分类关联与 DK/定制分类页双向同步；停用或删除后不再参与推荐。" type="info" show-icon />
      <div v-if="moduleKey==='stock-warning'" class="toolbar">
        库存低于 <a-input-number v-model="warningThreshold" :min="1" :max="1000000" :precision="0" /> 件时预警
        <a-button v-hasPermi="['dkmall:legacy:edit']" type="primary" @click="saveWarningThreshold">保存配置</a-button>
      </div>
      <div class="toolbar">
        <a-input-search v-model="query.keyword" allow-clear :placeholder="moduleKey === 'dialing-code' ? '国家中文名 / 英文名 / 区号 / 地区编码' : '输入关键词'" style="width:280px" @search="search" />
        <a-select v-if="moduleKey==='album'" v-model="query.scope" allow-clear placeholder="年份" style="width:160px" @change="search"><a-select-option v-for="y in yearOptions" :key="y.value" :value="y.value">{{ y.label }}</a-select-option></a-select>
        <a-radio-group v-if="scopeOptions.length" v-model="query.scope" button-style="solid" @change="search">
          <a-radio-button v-for="option in scopeOptions" :key="option.value" :value="option.value">{{ option.label }}</a-radio-button>
        </a-radio-group>
        <a-button v-if="moduleKey!=='stock-warning'" type="primary" v-hasPermi="['dkmall:legacy:edit']" @click="open()"><a-icon type="plus" />新增</a-button>
      </div>
      <a-table row-key="aid" bordered :loading="loading" :columns="columns" :data-source="rows" :pagination="false">
        <template slot="image" slot-scope="value,row"><a-avatar v-if="imageUrl(value,row)" shape="square" :size="52" :src="imageUrl(value,row)" /><span v-else>-</span></template>
        <template slot="file" slot-scope="value"><div v-if="fileEntries(value).length"><div v-for="(file,i) in fileEntries(value)" :key="i"><a :href="file.url" :title="file.name" target="_blank" rel="noopener noreferrer" class="pdf-link">{{ file.name }}</a></div></div><span v-else>--</span></template>
        <template slot="switch" slot-scope="value"><a-tag :color="Number(value) === 1 ? 'green' : 'red'">{{ Number(value) === 1 ? '启用' : '禁用' }}</a-tag></template>
        <template slot="albumYear" slot-scope="value">{{ yearLabel(value) }}</template>
        <template slot="categories" slot-scope="value"><span :title="categoryLabel(value)">{{ categoryLabel(value) }}</span></template>
        <template slot="region" slot-scope="value"><a-tag>{{ Number(value) === 2 ? '国际版（美元）' : '国内版（人民币）' }}</a-tag></template>
        <template slot="shopType" slot-scope="value"><a-tag>{{ Number(value) === 12 ? '定制商城' : '现货商城' }}</a-tag></template>
        <template slot="currency" slot-scope="value">{{ currencyLabel(value) }}</template>
        <template slot="color" slot-scope="value"><span class="color-value"><i class="color-swatch" :style="{ backgroundColor: value || '#ffffff' }" />{{ value || '-' }}</span></template>
        <template slot="operation" slot-scope="text, record">
          <a v-if="moduleKey==='stock-warning'" v-hasPermi="['dkmall:legacy:edit']" @click="openReplenish(record)">补货</a>
          <template v-else><a @click="open(record)">编辑</a><a-divider type="vertical" /><a-popconfirm title="确定删除这条配置？" @confirm="remove(record)"><a>删除</a></a-popconfirm></template>
        </template>
      </a-table>
      <a-pagination class="pager" show-size-changer show-quick-jumper :current="query.pageNum" :page-size="query.pageSize" :total="total" :show-total="n => `共 ${n} 条`" @change="changePage" @showSizeChange="changePage" />

      <dk-mall-legacy-module v-if="moduleKey==='environment'" module-override="manufacturing-process" />
      <a-modal title="补货" :visible="replenishVisible" :confirm-loading="saving" @ok="saveReplenish" @cancel="replenishVisible=false">
        <p>{{ replenishGoods.name }} · {{ replenishGoods.sn }}，当前库存 {{ replenishGoods.real_stock }}</p>
        <a-form-model-item label="补货数量"><a-input-number v-model="replenishQuantity" :min="1" :max="1000000" :precision="0" style="width:100%" /></a-form-model-item>
        <a-form-model-item label="补货备注"><a-textarea v-model="replenishRemark" /></a-form-model-item>
      </a-modal>
      <a-modal :title="form.aid ? `编辑${schema.title}` : `新增${schema.title}`" :visible="visible" width="780px" :confirm-loading="saving" @ok="save" @cancel="visible=false">
        <a-form-model ref="legacyForm" :model="form" :label-col="{span:5}" :wrapper-col="{span:18}">
          <a-form-model-item v-for="field in schema.fields" :key="field.name" :label="field.label" :prop="field.name" :rules="field.required ? [{ required:true, message:`请填写${field.label}` }] : []">
            <a-switch v-if="field.type === 'switch'" :checked="Number(form[field.name]) === 1" @change="v => $set(form, field.name, v ? 1 : 0)" />
            <editor v-else-if="field.type === 'richtext'" v-model="form[field.name]" :height="230" />
            <a-date-picker v-else-if="field.type === 'datetime'" :value="dateValue(form[field.name])" show-time style="width:100%" @change="v => $set(form, field.name, v ? v.unix() : 0)" />
            <a-select v-else-if="field.type === 'environment-config'" v-model="form[field.name]" placeholder="请选择环保配置"><a-select-option v-for="e in environmentOptions" :key="e.aid" :value="Number(e.aid)">{{ e.title }}</a-select-option></a-select>
            <a-input-number v-else-if="field.type === 'number'" v-model="form[field.name]" style="width:100%" />
            <div v-else-if="field.type === 'readonly' || field.type === 'generated'"><a-input :value="field.name === 'price_range' ? priceRangeLabel(form) : form[field.name]" disabled /><div v-if="field.name === 'price_range'" class="hint">系统根据起止价格自动生成，并按国内/国际版本显示币种，无需填写</div></div>
            <a-input v-else-if="field.type === 'color'" v-model="form[field.name]" type="color" />
            <div v-else-if="field.type === 'category-multi'">
              <a-tree-select :value="categoryValues(form[field.name]).map(value => ({ value, label: categoryNames[value] || `分类 #${value}` }))" :tree-data="categoryOptions" tree-checkable tree-check-strictly show-search tree-node-filter-prop="title" allow-clear placeholder="选择现货/定制商城一级分类，可多选" style="width:100%" @change="v => $set(form, field.name, v.map(n => n.value).join(','))" />
              <div class="hint">仅需关联一级分类，推荐范围自动包含其下全部子分类商品。</div>
              <div class="hint">选择上级分类自动包含其子分类。未关联分类时该偏好暂无推荐商品。</div>
            </div>
            <a-select
              v-else-if="field.type === 'album-year'"
              v-model="form[field.name]"
              show-search
              allow-clear
              option-filter-prop="children"
              placeholder="请选择年份"
              style="width:100%">
              <a-select-option v-for="opt in yearOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</a-select-option>
            </a-select>
            <a-select v-else-if="field.type === 'service-role'" v-model="form[field.name]"><a-select-option :value="1">现货客服</a-select-option><a-select-option :value="2">专属客服</a-select-option></a-select>
            <a-select v-else-if="field.type === 'cancel-side'" v-model="form[field.name]" placeholder="请选择适用端"><a-select-option :value="0">商城端</a-select-option><a-select-option :value="1">客服端</a-select-option></a-select>
            <a-select v-else-if="field.type === 'region'" v-model="form[field.name]" @change="syncCurrency"><a-select-option :value="1">国内版（人民币 ￥）</a-select-option><a-select-option :value="2">国际版（美元 $）</a-select-option></a-select>
            <a-select v-else-if="field.type === 'region-all'" v-model="form[field.name]"><a-select-option :value="0">全部</a-select-option><a-select-option :value="1">国内</a-select-option><a-select-option :value="2">国外</a-select-option></a-select>
            <a-select v-else-if="field.type === 'shop-type'" v-model="form[field.name]"><a-select-option :value="3">现货(DK)</a-select-option><a-select-option :value="12">定制(ODM)</a-select-option></a-select>
            <a-input v-else-if="field.type === 'currency'" :value="currencyLabel(form[field.name])" disabled />
            <a-textarea v-else-if="field.type === 'textarea'" v-model="form[field.name]" :rows="4" />
            <div v-else-if="field.type === 'image' || field.type === 'file'">
              <file-upload :value="uploadPreview(field)" :type="field.type === 'image' ? 'image' : 'file'" :count="1" :preserve-file-name="moduleKey==='album' && field.type==='file'" :max-size-mb="moduleKey==='album' && field.type==='file' ? 100 : 2" :accept="moduleKey==='album' && field.type==='file' ? '.pdf,application/pdf' : ''" @change="value => changeUpload(field.name, value)" />
              <div class="hint">{{ field.type === 'image' ? imageHint(moduleKey, field.name, form) : moduleKey==='album' ? '支持 PDF，单个文件最大 100MB' : '支持 PDF 等项目附件' }}</div>
            </div>
            <a-input v-else v-model="form[field.name]" :placeholder="field.name === 'area_code' ? '例如 +86、+1、+44' : (field.name === 'iso_code' ? '例如 CN、US；可选' : '')" />
          </a-form-model-item>
        </a-form-model>
      </a-modal>
    </a-card>
  </page-header-wrapper>
</template>

<script>
import request from '@/utils/request'
import moment from 'moment'
// Design dimensions are width × height in pixels; these are guidance, not upload restrictions.
function imageHint(moduleKey, fieldName, form = {}) {
  const sizes = {
    'recommendation-preference.picture': '157×122px',
    'navigation.thumb': '113×113px（关于我们、电子画册等服务入口）',
    'exhibition.picture': '224×280px',
    'environment.background': '375×325px（再生环保详情顶部图）',
    'manufacturing-process.label_diagram': '80×80px（制造流程图标）',
    'manufacturing-process.display_diagram': '80×80px（制造流程下方图）',
    'dialing-code.national_flag': '38×25px（宽×高），比例 38:25'
  }
  if (moduleKey === 'advertisement' && fieldName === 'thumb') {
    return Number(form.operationadstype_id) === 20
      ? '建议尺寸 375×557px（ODM 首页轮播），单张不超过 2MB'
      : '请按广告位置上传：探索顶部节日轮播 375×600px；ODM 首页轮播 375×557px；共享设计室顶部图 375×400px；再生环保入口图 331×150px。单张不超过 2MB'
  }
  if (moduleKey === 'recommendation-preference' && fieldName === 'picture') return '建议尺寸 157×122px（宽×高），比例 157:122；单张不超过 2MB'
  if (moduleKey === 'dialing-code' && fieldName === 'national_flag') {
    return '建议尺寸 38×25px（宽×高），比例 38:25；也可按相同比例上传更清晰图（如 76×50、114×75），单张不超过 2MB'
  }
  const size = sizes[`${moduleKey}.${fieldName}`]
  return size ? `建议尺寸 ${size}，单张不超过 2MB` : '请按 APP 展示比例上传，建议不超过 2MB'
}

import Editor from '@/components/Editor'
import { legacySchema, legacyPage, legacyDetail, addLegacy, updateLegacy, deleteLegacy } from '@/api/dkmall/legacy'
import { listCategories } from '@/api/dkmall/catalog'

export default {
  name: 'DkMallLegacyModule',
  components: { Editor },
  props: { moduleOverride: {type:String,default:''} },
  data() {
    return {
      warningThreshold: 50, replenishVisible: false, replenishGoods: {}, replenishQuantity: 1, replenishRemark: '',
      schema: { title: '', fields: [] },
      rows: [],
      total: 0,
      loading: false,
      saving: false,
      visible: false,
      form: {},
      yearOptions: [], environmentOptions: [],
      categoryOptions: [],
      categoryNames: {},
      query: { keyword: '', pageNum: 1, pageSize: 20 }
    }
  },
  computed: {
    moduleKey() { return this.moduleOverride || this.$route.query.module || String(this.$route.path).split('/').filter(Boolean).pop() },
    scopeOptions() {
      if (this.moduleKey === 'custom-order-fee') return [{ value:1,label:'现货客服费用' },{ value:2,label:'专属客服费用' }]
      if (this.moduleKey === 'delivery-time') return [{ value: 3, label: '现货商城' }, { value: 12, label: '定制商城' }]
      if (this.moduleKey === 'price-range') return [{ value: 1, label: '国内版（人民币）' }, { value: 2, label: '国际版（美元）' }]
      if (this.moduleKey === 'service-order-cancel-reason') return [{ value: -1, label: '全部' }, { value: 0, label: '商城端' }, { value: 1, label: '客服端' }]
      return []
    },
    columns() {
      const visibleFields = (this.schema.fields || []).filter(f => !['textarea','richtext'].includes(f.type)).slice(0, 7)
      return (['album','album-year','environment','manufacturing-process'].includes(this.moduleKey) ? [] : [{ title: 'ID', dataIndex: 'aid', width: 80 }]).concat(visibleFields.map(field => {
        let slot
        if (field.type === 'image' || field.type === 'switch' || field.type === 'region' || field.type === 'currency' || field.type === 'color') slot = field.type
        else if (field.type === 'shop-type') slot = 'shopType'
        else if (field.type === 'album-year') slot = 'albumYear'
        else if (field.type === 'category-multi') slot = 'categories'
        else if (field.type === 'service-role') return {title:field.label || field.title || '适用客服',dataIndex:field.name,customRender:v=>Number(v)===1?'现货客服':'专属客服'}
        else if (field.type === 'cancel-side') return {title:field.label || '适用端',dataIndex:field.name,width:100,customRender:v=>Number(v)===1?'客服端':'商城端'}
        else if (field.type === 'file' && this.moduleKey==='album') slot = 'file'
        return {
          title: field.label,
          dataIndex: field.name,
          ellipsis: field.type!=='file',
          scopedSlots: slot ? { customRender: slot } : undefined
        }
      })).concat([{ title: '操作', scopedSlots: { customRender: 'operation' }, width: 130 }])
    }
  },
  watch: { '$route.fullPath': 'initialize' },
  created() { this.initialize() },
  methods: {
    imageHint,
    fileEntries(value) {
      if(!value)return []
      let entries=value
      if(typeof value==='string'){try{entries=JSON.parse(value)}catch(_){if(value.trim().startsWith('[')||value.trim().startsWith('{'))return [];entries=value}}
      if(!Array.isArray(entries))entries=[entries]
      const base=(typeof process!=='undefined'&&process.env&&process.env.VUE_APP_LEGACY_FILE_BASE_URL)||'http://app.dkinggroup.cn'
      return entries.map(item=>{
        let url=typeof item==='string'?item:(item&&(item.path||item.url||item.thumb))||''
        if(!url)return null
        let name=(typeof item==='object'&&(item.name||item.filename))||url.split(/[?#]/)[0].split('/').pop()||'PDF 文件'
        try{name=decodeURIComponent(name)}catch(_){}
        if(url.startsWith('//'))url='https:'+url
        else if(!/^https?:\/\//i.test(url)){if(url.includes(':'))return null;url=base.replace(/\/+$/,'')+'/'+url.replace(/^\/+/, '')}
        return {name,url}
      }).filter(Boolean)
    },
    imageUrl(value,row) { return this.moduleKey==='navigation' && row && row.thumb_url!==undefined ? row.thumb_url : value },
    uploadPreview(field) { if(this.moduleKey==='album'&&field.type==='file')return JSON.stringify(this.fileEntries(this.form[field.name]));return this.moduleKey==='navigation' && field.name==='thumb' ? (this.imageUrl(this.form.thumb,this.form) || '') : (this.form[field.name] || '') },
    dateValue(value) { return Number(value)>0 ? moment.unix(Number(value)) : null },
    saveWarningThreshold() { return request({url:'/dkmall/legacy/stock-warning/threshold',method:'put',data:{threshold:this.warningThreshold}}).then(() => {this.$message.success('配置已保存');this.load()}) },
    openReplenish(record) { this.replenishGoods=record;this.replenishQuantity=1;this.replenishRemark='';this.replenishVisible=true },
    saveReplenish() { if(!Number.isInteger(this.replenishQuantity)||this.replenishQuantity<1)return this.$message.warning('请输入有效补货数量');this.saving=true;request({url:`/dkmall/legacy/stock-warning/${this.replenishGoods.aid}/replenish`,method:'post',data:{quantity:String(this.replenishQuantity),remark:this.replenishRemark}}).then(() => {this.replenishVisible=false;this.$message.success('补货已完成并记录库存日志');this.load()}).finally(() => {this.saving=false}) },
    initialize() {
      if(this.moduleKey==='stock-warning') request({url:'/dkmall/legacy/stock-warning/threshold',method:'get'}).then(r => {this.warningThreshold=Number(r.data||50)})
      const defaultScope = this.moduleKey === 'delivery-time' ? 3
        : (this.moduleKey === 'service-order-cancel-reason' ? -1
          : (['price-range','custom-order-fee'].includes(this.moduleKey) ? 1 : undefined))
      this.query = { keyword: '', scope: defaultScope, pageNum: 1, pageSize: 20 }
      legacySchema(this.moduleKey).then(res => {
        this.schema = res.data || { fields: [] }
        if(this.moduleKey==='manufacturing-process')legacyPage('environment',{pageNum:1,pageSize:100}).then(r=>{this.environmentOptions=(r.data&&r.data.rows)||[]})
        this.loadYearOptionsIfNeeded()
        this.loadCategoryOptionsIfNeeded()
        this.load()
      })
    },
    loadCategoryOptionsIfNeeded() {
      if (this.moduleKey !== 'recommendation-preference') { this.categoryOptions = []; this.categoryNames = {}; return }
      return Promise.all([listCategories('DK'), listCategories('CUSTOM')]).then(results => {
        const names = {}
        this.categoryOptions = results.map((res, index) => {
          const rows = res.data || []
          rows.forEach(r => { names[String(r.aid)] = `${r.name} (${r.nameEn || r.name_en || '-'})` })
          const roots = rows.filter(r => Number(r.pid) === 0).map(r => ({ value: String(r.aid), key: String(r.aid), title: names[String(r.aid)] }))
          return { value: `mall-${index}`, key: `mall-${index}`, title: index === 0 ? '现货商城' : '定制商城', selectable: false, disableCheckbox: true, children: roots }
        })
        // Preserve labels for historical child associations without offering them as new choices.
        this.categoryNames = names
      }).catch(() => { this.$message.error('分类加载失败，请刷新后重试') })
    },
    categoryValues(value) { return Array.isArray(value) ? value.map(String) : String(value || '').split(',').filter(Boolean) },
    categoryLabel(value) {
      return this.categoryValues(value).map(id => this.categoryNames[id] || `分类 #${id}`).join('、') || '未关联'
    },
    loadYearOptionsIfNeeded() {
      const need = (this.schema.fields || []).some(f => f.type === 'album-year')
      if (!need) {
        this.yearOptions = []
        return
      }
      legacyPage('album-year', { pageNum: 1, pageSize: 100 }).then(res => {
        const rows = (res.data && res.data.rows) || []
        this.yearOptions = rows.map(r => ({
          value: Number(r.aid),
          label: String(r.particular_year || r.aid)
        }))
      })
    },
    yearLabel(value) {
      if (value === undefined || value === null || value === '') return '-'
      const hit = this.yearOptions.find(o => Number(o.value) === Number(value))
      return hit ? hit.label : String(value)
    },
    currencyLabel(value) { return value === 'USD' ? 'USD 美元（$）' : 'CNY 人民币（￥）' },
    priceRangeLabel(row) {
      const starts = row.price_starts, end = row.price_end
      if (starts === undefined || starts === null || starts === '' || end === undefined || end === null || end === '') return ''
      const unit = Number(row.region) === 2 ? '$' : '￥'
      return `${unit}${Number(starts)}-${unit}${Number(end)}`
    },
    syncCurrency(region) { this.$set(this.form, 'currency', Number(region) === 2 ? 'USD' : 'CNY') },
    load() {
      this.loading = true
      legacyPage(this.moduleKey, this.query).then(res => {
        this.rows = (res.data && res.data.rows) || []
        this.total = Number((res.data && res.data.total) || 0)
      }).finally(() => { this.loading = false })
    },
    search() { this.query.pageNum = 1; this.load() },
    changePage(page, pageSize) { this.query.pageNum = page; this.query.pageSize = pageSize; this.load() },
    open(record) {
      if (!record) {
        this.form = {}
        this.schema.fields.forEach(f => {
          if (f.type === 'switch') this.$set(this.form, f.name, f.name === 'status' ? 1 : 0)
          else if (f.type === 'service-role') this.$set(this.form, f.name, this.query.scope || 1)
          else if (f.type === 'cancel-side') this.$set(this.form, f.name, (this.query.scope === 0 || this.query.scope === 1) ? this.query.scope : 0)
          else if (f.type === 'shop-type') this.$set(this.form, f.name, this.query.scope || 3)
          else if (f.type === 'currency') this.$set(this.form, f.name, Number(this.query.scope) === 2 ? 'USD' : 'CNY')
          else if (f.type === 'region') this.$set(this.form, f.name, this.query.scope || 1)
          else if (f.type === 'album-year') this.$set(this.form, f.name, undefined)
          else if (f.type === 'environment-config') this.$set(this.form, f.name, this.environmentOptions.length===1 ? Number(this.environmentOptions[0].aid) : undefined)
        })
        this.visible = true
        return
      }
      legacyDetail(this.moduleKey, record.aid).then(res => {
        const data = { ...(res.data || {}) }
        if (data.freeyearconfiguration_id !== undefined && data.freeyearconfiguration_id !== null && data.freeyearconfiguration_id !== '') {
          data.freeyearconfiguration_id = Number(data.freeyearconfiguration_id)
        }
        if(data.freeepbc_id)data.freeepbc_id=Number(data.freeepbc_id)
        if(data.is_kf!==undefined&&data.is_kf!==null&&data.is_kf!=='')data.is_kf=Number(data.is_kf)
        this.form = data
        this.visible = true
      })
    },
    changeUpload(name, value) { this.$set(this.form, name, typeof value === 'string' ? value : ((value && value.path) || ''));if(this.moduleKey==='navigation'&&name==='thumb')this.$delete(this.form,'thumb_url') },
    save() {
      this.$refs.legacyForm.validate(valid => {
        if (!valid) return
        this.saving = true
        const req = this.form.aid ? updateLegacy(this.moduleKey, this.form) : addLegacy(this.moduleKey, this.form)
        req.then(() => {
          this.$message.success('保存成功')
          this.visible = false
          this.load()
        }).finally(() => { this.saving = false })
      })
    },
    remove(record) { deleteLegacy(this.moduleKey, record.aid).then(() => { this.$message.success('删除成功'); this.load() }) }
  }
}
</script>

<style scoped>
.dialing-hint { margin-top:12px; }
.toolbar { display:flex; gap:12px; margin:16px 0; }
.pager { margin-top:16px; text-align:right; }
.hint { color:#8c8c8c; font-size:12px; }
.color-value { display:inline-flex; align-items:center; gap:8px; }
.color-swatch { width:22px; height:22px; border-radius:4px; border:1px solid #d9d9d9; display:inline-block; }
</style>
